'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { TypeIcon } from 'src/components';
import { getProfileUrl } from 'src/utils/profileUrl';
import type { ProviderNameCellContext } from './index';

interface ProviderNameCellProps {
  row: any;
  context?: ProviderNameCellContext;
}

/**
 * ProviderNameCell component displays provider information with conditional navigation.
 * Routes to referrals tab for HCP providers when in specific contexts (ReferringOutTable
 * on center/clinic profiles), otherwise routes to provider overview.
 */
const ProviderNameCell: React.FC<ProviderNameCellProps> = ({
  row,
  context,
}) => {
  const provider = useMemo(() => {
    return row.provider ? row.provider : row;
  }, [row]);

  const type =
    provider?.object_type?.toLowerCase() ||
    provider?.content_type ||
    provider?.type;

  /**
   * Determine the target path for navigation based on context and provider type.
   * Routes to referrals tab for HCP providers when shouldRouteToReferrals is true,
   * otherwise routes to the provider overview page.
   */
  const targetPath = useMemo(() => {
    const shouldRouteToReferrals =
      context?.shouldRouteToReferrals && type === 'hcp';
    const basePath = getProfileUrl(type || 'hcp', provider.provider_id);

    return shouldRouteToReferrals
      ? `${basePath}/patient_patterns?relationship=outbound`
      : basePath;
  }, [context?.shouldRouteToReferrals, type, provider.provider_id]);

  const useReplace = useMemo(() => {
    // Don't replace history when routing to referrals - users expect to go back
    const isRoutingToReferrals =
      context?.shouldRouteToReferrals && type === 'hcp';
    return !isRoutingToReferrals;
  }, [context?.shouldRouteToReferrals, type]);

  return (
    <div
      className="text-sm font-semibold overflow-hidden truncate"
      onClick={(e) => e.stopPropagation()}
      data-testid="system-name-cell"
    >
      <div
        id={`provider-name-cell-${provider.provider_id}`}
        className="flex items-center w-full gap-2"
      >
        <div className="flex items-center justify-start gap-2 w-full overflow-hidden truncate">
          <div className="flex items-center w-5 h-full">
            <TypeIcon type={type} className="text-sky-700" />
          </div>
          <div className="text-sm font-medium overflow-hidden text-ellipsis [&>p]:text-gray-600">
            <Link
              href={targetPath}
              replace={useReplace}
              scroll={false}
              className="text-sky-900"
              data-testid={`provider-name-cell-link-${provider.provider_id}`}
            >
              <span>{provider.name || 'n/a'}</span>
              <Icon
                icon={faExternalLinkAlt}
                size="sm"
                className="ml-1"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderNameCell;
